import { XMarkIcon } from '@heroicons/react/16/solid';
import { useEffect } from 'react';
import { Button, Divider, Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { useIntegrationsContext } from '../../../../../providers/IntegrationsProvider/Integrations.context.ts';
import { eIntegrationStatus } from '../../../../../providers/IntegrationsProvider/Integrations.enums.ts';
import { useModalsContext } from '../../../../../providers/ModalsProvider/Modals.context.ts';
import MetaStepAllPages from './components/MetaStepAllPages/MetaStepAllPages.component.tsx';
import MetaStepDisconnect from './components/MetaStepDisconnect/MetaStepDisconnect.component.tsx';
import MetaStepInfo from './components/MetaStepInfo/MetaStepInfo.component.tsx';
import { useMetaConnectionModalContext } from './MetaConnectionModal.hook.ts';
import './MetaConnectionModal.styles.scss';

// At module scope: declared inside MetaConnectionModal it was a new component
// type on every render, so React remounted the step (and reloaded the video)
// on each render, including the one that starts the exit (DES-222).
function MetaConnectionModalBody({ step }: { step: number }) {
  switch (step) {
    case 1:
      return (
        <iframe
          className="MetaConnectionModal__Iframe"
          src="https://www.youtube.com/embed/Xr1OxGSUJjc"
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      );
    case 2:
      return <MetaStepInfo />;
    case 3:
      return <MetaStepAllPages />;
    case 4:
      return <MetaStepDisconnect />;
    default:
      return <MetaStepInfo />;
  }
}

export default function MetaConnectionModal() {
  const {
    setMetaConnectionModalIsOpen,
    metaConnectionModalIsOpen,
    metaConnectionModalReconnectRequested,
    setMetaConnectionModalReconnectRequested,
  } = useModalsContext();
  const { facebookIntegration } = useIntegrationsContext();
  const { step, setStep } = useMetaConnectionModalContext();
  const { t } = useTranslation('dashboard/dashboard');

  useEffect(() => {
    // INT-1522: the meta-token-invalid alert requests this modal open at
    // the reconnect step (MetaStepInfo) even though the integration still
    // reads CONNECTED -- the account was never formally disconnected, only
    // its token stopped working. `metaConnectionModalReconnectRequested`
    // lives on ModalsContext, not this modal's own
    // MetaConnectionModalContext (JJ, INT-1522 review): that provider is
    // mounted only around this component, a sibling of ModalsProvider's
    // `{children}` where the alert CTA hook actually runs, so a `setStep`
    // called from there could never reach this component's real state --
    // see MetaConnectionModa.types.ts for the full tree-shape explanation.
    //
    // INT-1522 (JJ/Daniel, non-blocking follow-up, reproduced in staging):
    // skip entirely while closed. The closing effect below clears
    // metaConnectionModalReconnectRequested the instant the modal starts
    // closing, which used to re-run this effect mid-close and land on
    // setStep(4) -- ModalPresence keeps the modal mounted for its exit
    // animation (~MODAL_EXIT_MS), so MetaStepDisconnect's "Desconectar
    // Integración" flashed for that whole window before the modal finished
    // fading out.
    if (!metaConnectionModalIsOpen) return;
    if (metaConnectionModalReconnectRequested) {
      setStep(2);
      return;
    }
    if (facebookIntegration.state === eIntegrationStatus.CONNECTED) {
      setStep(4);
    }
  }, [
    metaConnectionModalIsOpen,
    facebookIntegration.state,
    metaConnectionModalReconnectRequested,
  ]);

  // Reset once the modal closes, so the NEXT open (e.g. a normal click on
  // the Dashboard's Facebook/Instagram cards) gets the default
  // auto-jump-to-disconnect behaviour rather than staying pinned open.
  useEffect(() => {
    if (!metaConnectionModalIsOpen && metaConnectionModalReconnectRequested) {
      setMetaConnectionModalReconnectRequested(false);
    }
  }, [metaConnectionModalIsOpen, metaConnectionModalReconnectRequested]);

  function buttonAction(step: number) {
    switch (step) {
      case 1:
        return (
          <Button
            color="primary"
            className="text-base-100"
            onClick={() => setStep(2)}
          >
            Siguiente
          </Button>
        );
      case 3:
        return (
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              color="primary"
              onClick={() =>
                setMetaConnectionModalIsOpen(!metaConnectionModalIsOpen)
              }
            >
              Regresar
            </Button>
          </div>
        );
      default:
        return null;
    }
  }

  return (
    <Modal
      open={metaConnectionModalIsOpen}
      responsive={step !== 1}
      className="MetaConnectionModal w-full max-w-5xl "
    >
      <Modal.Header>
        <div className="MetaConnectionModal__Header">
          <h2 className="MetaConnectionModal__Header__Title">
            {(step === 1 && t('components.modals.MetaConnection.videoTitle')) ||
              (step === 3 &&
                t(
                  'components.modals.MetaConnection.confirmIntegrationsTitle',
                )) ||
              null}
          </h2>
          <XMarkIcon
            onClick={() => {
              setMetaConnectionModalIsOpen(!metaConnectionModalIsOpen);
            }}
            className="MetaConnectionModal__Header__Xicon"
          />
        </div>
        {step === 3 ? <Divider /> : null}
      </Modal.Header>
      <Modal.Body>
        <MetaConnectionModalBody step={step} />
      </Modal.Body>
      <Modal.Actions>{buttonAction(step)}</Modal.Actions>
    </Modal>
  );
}
